import React, { useState, useEffect } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import Loader from "../../components/common/Loader";
import AddAboutPageModal from "../../components/modals/AddAboutPageModal";
import EditAboutPageModal from "../../components/modals/EditAboutPageModal";
import { FaEdit, FaTrash } from "react-icons/fa";
import WelcomeCard from "../../components/common/WelcomeCard";

const AboutPage = () => {
  const [contentList, setContentList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [openAddModal, setOpenAddModal] = useState(false);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [selectedContent, setSelectedContent] = useState(null);

  const fetchAboutPageContent = async () => {
    setLoading(true);
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/about/get-about`,
      );
      const data = Array.isArray(res.data.data) ? res.data.data : [];
      setContentList(data);
    } catch (err) {
      console.error(err);
      setError("Failed to load About Page content");
      toast.error("Failed to load About Page content");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAboutPageContent();
  }, []);

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure to delete this content?")) return;
    try {
      await axios.delete(
        `${process.env.REACT_APP_API_URL}/about/delete-about/${id}`,
      );
      setContentList(contentList.filter((c) => c._id !== id));
      toast.success("Deleted successfully");
    } catch (err) {
      console.error(err);
      toast.error("Failed to delete content");
    }
  };

  const handleContentAdded = (newContent) => {
    if (!newContent) return;
    setContentList((prev) => [newContent, ...prev]);
  };

  const handleContentUpdated = (updatedContent) => {
    if (!updatedContent) return;
    setContentList((prev) =>
      prev.map((c) => (c._id === updatedContent._id ? updatedContent : c)),
    );
    toast.success("Updated successfully");
  };

  return (
    <div className="p-4">
      <WelcomeCard />

      {/* Add/Edit Modals */}
      <AddAboutPageModal
        isOpen={openAddModal}
        onClose={() => setOpenAddModal(false)}
        onContentAdded={handleContentAdded}
      />

      {selectedContent && (
        <EditAboutPageModal
          isOpen={openEditModal}
          onClose={() => setOpenEditModal(false)}
          content={selectedContent}
          onContentUpdated={handleContentUpdated}
        />
      )}

      <div className="flex justify-between items-center mb-4">
        <h1 className="text-xl font-semibold">About Page Content</h1>
        <button
          onClick={() => setOpenAddModal(true)}
          className="bg-orange-500 text-white px-4 py-2 rounded hover:bg-orange-600 flex items-center gap-2"
        >
          + Add Content
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center items-center mt-10">
          <Loader />
        </div>
      ) : contentList.length === 0 ? (
        <div className="text-center text-gray-500 mt-10">No content found</div>
      ) : (
        <div className="grid grid-cols-1">
          {contentList.map((content) => (
            <div
              key={content._id}
              className="border rounded shadow relative p-4"
            >
              <div className="grid grid-cols-2">
                <div>
                  <h2 className="font-bold text-lg">{content.title}</h2>
                </div>
                <div>
                  <div className="flex justify-end gap-2 mt-3">
                    <button
                      onClick={() => {
                        setSelectedContent(content);
                        setOpenEditModal(true);
                      }}
                      className="text-blue-500 hover:text-blue-700"
                    >
                      <FaEdit size={24} />
                    </button>
                  </div>
                </div>
              </div>

              <h3 className="text-orange-500">{content.subtitle}</h3>
              {/* Description */}
              {content.description && (
                <div
                  className="text-gray-600 mt-2"
                  dangerouslySetInnerHTML={{ __html: content.description }}
                />
              )}

              {/* Images */}
              {content.images?.map((img, i) => (
                <img
                  key={i}
                  src={img}
                  alt={`about-${i}`}
                  className="w-full h-64 object-cover mt-2 rounded"
                />
              ))}

              {/* Key Points */}
              {content.keyPoints?.length > 0 && (
                <div className="mt-2">
                  <h4 className="font-semibold">Key Points</h4>
                  {content.keyPoints.map((kp, i) => (
                    <ul key={i} className="list-disc ml-4">
                      <li>
                        <strong>{kp.heading}:</strong>
                        <ul className="list-inside ml-2">
                          {kp.points?.map((p, j) => (
                            <li
                              key={j}
                              dangerouslySetInnerHTML={{ __html: p }}
                            />
                          ))}
                        </ul>
                      </li>
                    </ul>
                  ))}
                </div>
              )}

              {/* Values / Third Cards Section */}
              {content.valuesSection && (
                <div className="mt-6 border-t pt-4">
                  <h4 className="font-semibold text-lg text-gray-800 mb-2">
                    Values Section (Third Cards)
                  </h4>

                  {/* Main Heading */}
                  <p className="font-medium text-orange-600 mb-3">
                    {content.valuesSection.mainHeading}
                  </p>

                  {/* Cards */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {content.valuesSection.cards?.map((card, index) => (
                      <div
                        key={index}
                        className="border rounded p-3 bg-gray-50"
                      >
                        <h5 className="font-semibold text-gray-800">
                          {card.heading}
                        </h5>
                        <p className="text-gray-600 text-sm mt-1">
                          {card.description}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AboutPage;
